import { Suspense, lazy, useEffect, useState } from "react"; // App entry point
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import { useSeoConfig } from "@/hooks/useSeoConfig";
import { useContentModerationConfig } from "@/hooks/useContentModerationConfig";
import { prefetchAuthenticatedRoutes } from "@/lib/routePrefetch";
import Layout from "@/components/Layout";
import { useDeviceType } from "@/hooks/use-device-type";

// Critical pages - loaded immediately for fast initial render
import Index from "./pages/Index";
import Maintenance from "./pages/Maintenance";
import NotFound from "./pages/NotFound";

// Lazy load non-critical pages
const Upload = lazy(() => import("./pages/Upload"));
const Preise = lazy(() => import("./pages/Preise"));
const Demo = lazy(() => import("./pages/Demo"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Gesetzesdatenbank = lazy(() => import("./pages/Gesetzesdatenbank"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const Widerruf = lazy(() => import("./pages/Widerruf"));
const Barrierefreiheit = lazy(() => import("./pages/Barrierefreiheit"));
const About = lazy(() => import("./pages/About"));
const Vorteile = lazy(() => import("./pages/Vorteile"));
const KennzeichnungAlles = lazy(() => import("./pages/KennzeichnungAlles"));
const Admin = lazy(() => import("./pages/Admin"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Analyseergebnisse = lazy(() => import("./pages/Analyseergebnisse"));
const Profil = lazy(() => import("./pages/Profil"));
const Auth = lazy(() => import("./pages/Auth"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 Minuten
      gcTime: 1000 * 60 * 30, // 30 Minuten Cache
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

// Loading fallback component (Vollbild – nur für initialen Auth-Check)
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Laden...</div>
  </div>
);

// Verzögerter Fallback: rendert für die ersten ~220 ms nichts. Wenn der lazy
// Chunk innerhalb dieser Zeit geladen ist (was bei vorgeladenen Routen fast
// immer der Fall ist), sieht der Nutzer keinerlei Lade-UI – kein weißes
// Aufblitzen mehr beim Seitenwechsel.
const RouteFallback = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 220);
    return () => clearTimeout(t);
  }, []);
  if (!show) return null;
  return (
    <div
      aria-hidden
      className="fixed top-0 left-0 right-0 h-0.5 bg-primary/70 animate-pulse z-[60]"
    />
  );
};

// Protected Route Component - redirects to login if not logged in
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isLoggedIn, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isLoggedIn) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

// App Routes Component (needs to be inside AuthProvider)
const AppRoutes = () => {
  const { isLoggedIn, isLoading: isAuthLoading, isAdmin, canBypassPayment } = useAuth();
  const { isMaintenanceMode, isLoading: isMaintenanceLoading } = useMaintenanceMode();
  useSeoConfig();
  useContentModerationConfig();

  // Sobald der Auth-Status feststeht und der Nutzer eingeloggt ist, lädt der
  // Browser im Idle-Slot die Chunks für Profil/Analyseergebnisse/Admin vor.
  useEffect(() => {
    if (!isAuthLoading && isLoggedIn) {
      prefetchAuthenticatedRoutes();
    }
  }, [isAuthLoading, isLoggedIn]);

  // Fail-open: Don't block rendering while checking maintenance mode.
  // Render normal content immediately for fast FCP; switch to maintenance
  // only after the check confirms it's active.

  // Wenn Wartungsmodus aktiv und User kein Admin ist, zeige Wartungsseite
  // Admins müssen sich einloggen können, daher Auth-Loading berücksichtigen
  if (isMaintenanceMode && !isAuthLoading && !isAdmin && !canBypassPayment) {
    return (
      <Routes>
        <Route path="*" element={<Maintenance />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public route - Homepage - Kein Loading-Block nötig */}
      <Route path="/" element={<Index />} />
      
      {/* Auth route - public */}
      <Route path="/auth" element={isLoggedIn ? <Navigate to="/" replace /> : <Auth />} />
      
      {/* Protected app routes */}
      <Route path="/speisekarte-hochladen" element={<ProtectedRoute><Upload /></ProtectedRoute>} />
      <Route path="/preise" element={<Preise />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/gesetzesdatenbank" element={<Gesetzesdatenbank />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/impressum" element={<Impressum />} />
      <Route path="/datenschutz" element={<Datenschutz />} />
      <Route path="/agb" element={<AGB />} />
      <Route path="/widerruf" element={<Widerruf />} />
      <Route path="/barrierefreiheit" element={<Barrierefreiheit />} />
      <Route path="/vorteile" element={<Vorteile />} />
      <Route path="/about" element={<About />} />
      <Route path="/kennzeichnung" element={<KennzeichnungAlles />} />
      <Route path="/kennzeichnung-alles" element={<KennzeichnungAlles />} />
      <Route path="/wissen" element={<KennzeichnungAlles />} />
      <Route path="/analyseergebnisse" element={<ProtectedRoute><Analyseergebnisse /></ProtectedRoute>} />
      <Route path="/pruefungsergebnisse" element={<Navigate to={`/analyseergebnisse${typeof window !== 'undefined' ? window.location.search : ''}`} replace />} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/kontakt" element={<Kontakt />} />
      <Route path="/profil" element={<ProtectedRoute><Profil /></ProtectedRoute>} />
      
      {/* Legacy route redirect */}
      <Route path="/app" element={<Navigate to="/" replace />} />
      
      {/* Catch-all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <PersistentShell />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;

/**
 * Persistent Layout-Shell: Header/Footer werden auf App-Ebene gerendert und
 * bleiben beim Routenwechsel stabil. Innerhalb der Pages eingebettete
 * <Layout>-Wrapper erkennen den umgebenden Shell und verhalten sich als
 * Passthrough – dadurch entstehen keine doppelten Chrome-Elemente und kein
 * weißes Aufblitzen beim Wechsel.
 */
const PersistentShell = () => {
  const { isMobile } = useDeviceType();
  if (isMobile) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <AppRoutes />
      </Suspense>
    );
  }
  return (
    <Layout>
      <Suspense fallback={<RouteFallback />}>
        <AppRoutes />
      </Suspense>
    </Layout>
  );
};
